import {
  Table,
  Box,
  StatusIndicator,
  Alert,
  Button,
  PropertyFilter,
  Pagination,
  PropertyFilterProps,
} from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import TableHeader from "../common/components/elements/TableHeader";
import usePaginationHandler from "../common/hooks/PaginationHandler";
import {
  useTablePreferences,
  ColumnDefinition,
} from "../common/components/elements/TablePreferences";
import { DeleteCampaignRes, GetCampaignRes } from "../openapi-generated/lmpSchemas";
import {
  fetchDeleteCampaign,
  fetchDisableCampaign,
  fetchGetCampaign,
  fetchEnableCampaign,
  fetchLaunchCampaign,
  fetchAddCampaign,
} from "../openapi-generated/lmpComponents";
import ConfirmModal from "../common/components/elements/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { useNotificationBar } from "../common/hooks/NotificationsBar";
import { useCollection } from "@cloudscape-design/collection-hooks";
import { TableEmptyState, TableNoMatchState } from "../common/components/elements/Empty";
import { useGlobal } from "../common/hooks/GlobalContext";
import { CampaignStatus } from "../Constants";

import { fetchAllPages, toCsv, downloadCsvFile } from "../common/hooks/Utils";

const CampaignsPage = () => {
  const notificationBar = useNotificationBar();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<GetCampaignRes[]>([]);
  const { globalUser, setGlobalUser } = useGlobal();

  const [downloadLoading, setDownloadLoading] = useState(false);

  const [allCampaignsForFilter, setAllCampaignsForFilter] = useState<GetCampaignRes[]>([]);
  const [isFetchingAllForFilter, setIsFetchingAllForFilter] = useState(false);

  const i18nStrings: PropertyFilterProps.I18nStrings = {
    filteringAriaLabel: "Filter campaigns",
    dismissAriaLabel: "Close filter",
    clearAriaLabel: "Clear filter input",
    filteringPlaceholder: "Find campaigns by ID, name, area, or status",
    groupValuesText: "Values",
    groupPropertiesText: "Properties",
    operatorsText: "Operators",
    operationAndText: "And",
    operationOrText: "Or",
    operatorLessText: "Less than",
    operatorLessOrEqualText: "Less than or equal",
    operatorGreaterText: "Greater than",
    operatorGreaterOrEqualText: "Greater than or equal",
    operatorContainsText: "Contains",
    operatorDoesNotContainText: "Does not contain",
    operatorEqualsText: "Equals",
    operatorDoesNotEqualText: "Does not equal",
    editTokenHeader: "Edit filter",
    propertyText: "Property",
    operatorText: "Operator",
    valueText: "Value",
    cancelActionText: "Cancel",
    applyActionText: "Apply",
    allPropertiesLabel: "All properties",
    tokenLimitShowMore: "Show more",
    tokenLimitShowFewer: "Show fewer",
    clearFiltersText: "Clear filters",
    tokenOperatorAriaLabel: "Operator",
    removeTokenButtonAriaLabel: () => "Remove filter token",
    enteredTextLabel: (text: string) => `Use: ${text}`,
  };

  const filteringProperties: ReadonlyArray<PropertyFilterProps.FilteringProperty> = [
    {
      key: "CampaignId",
      propertyLabel: "ID",
      groupValuesLabel: "Campaign IDs",
      operators: ["=", ":"],
    },
    {
      key: "Name",
      propertyLabel: "Name",
      groupValuesLabel: "Campaign names",
      operators: ["=", ":"],
    },

    {
      key: "Area",
      propertyLabel: "Area",
      groupValuesLabel: "Areas",
      operators: ["=", ":"],
    },

    {
      key: "State",
      propertyLabel: "Status",
      groupValuesLabel: "Statuses",
      operators: ["=", ":"],
    },
  ];

  const columnDefinitions: ColumnDefinition<any>[] = [
    {
      id: "campaign_id",
      header: "ID",
      visible: true,
      sortingField: "CampaignId",
      minWidth: 80,
      cell: (c) => c.CampaignId,
    },
    {
      id: "campaign_name",
      header: "Name",
      visible: true,
      sortingField: "Name",
      minWidth: 150,
      cell: (c) => (
        <Button
          variant="link"
          onClick={() => {
            navigate("/details", { state: [c] });
          }}
        >
          {c.Name}
        </Button>
      ),
    },
    {
      id: "area",
      header: "Area",
      visible: globalUser!.Role == "Admin",
      minWidth: 100,
      cell: (c) => c.Area,
    },
    {
      id: "leads_coll",
      header: "Leads collected",
      visible: true,
      sortingField: "LeadsColl",
      minWidth: 130,
      cell: (c) => c.LeadsColl,
    },
    {
      id: "leads_disp",
      header: "Leads dispatched",
      visible: true,
      sortingField: "LeadsDisp",
      minWidth: 130,
      cell: (c) => c.LeadsDisp,
    },
    {
      id: "leads_lost",
      header: "Leads lost",
      visible: true,
      sortingField: "LeadsLost",
      minWidth: 110,
      cell: (c) => (
        <StatusIndicator type={c.LeadsLost == 0 ? "success" : "error"}>
          {c.LeadsLost}
        </StatusIndicator>
      ),
    },
    {
      id: "status",
      header: "Status",
      visible: true,
      minWidth: 100,
      cell: (c) => (
        <StatusIndicator
          type={
            c.State === CampaignStatus.Active
              ? "success"
              : c.State === CampaignStatus.Draft
                ? "in-progress"
                : "error"
          }
        >
          {c.State}
        </StatusIndicator>
      ),
    },
    {
      id: "user",
      header: "Last Modified by",
      visible: true,
      minWidth: 150,
      cell: (c) => c.User,
    },
  ];

  const queryP = globalUser?.Role != "Admin" ? { Area: globalUser?.Area } : {};
  const { collectionPreferences, preferences } = useTablePreferences({
    pageSize: 20,
    contentDensity: "comfortable",
    columnDefinitions,
    onConfirm: () => setSelectedItems([]),
  });

  const { pagination, currentPage, isLoading, resetPagination } = usePaginationHandler({
    api: fetchGetCampaign,
    pageSize: preferences.pageSize || 20,
    preInitialize: true,
    variables: { queryParams: queryP },
  });

  const [collectionSource, setCollectionSource] = useState<GetCampaignRes[]>([]);

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    propertyFilterProps,
    paginationProps,
  } = useCollection(collectionSource ?? [], {
    propertyFiltering: {
      filteringProperties,
      empty: <TableEmptyState resourceName="Campaign" />,
      noMatch: (
        <TableNoMatchState
          onClearFilter={() => actions.setPropertyFiltering({ tokens: [], operation: "or" })}
        />
      ),
    },
    sorting: {
      defaultState: {
        sortingColumn: columnDefinitions[1],
        isDescending: false,
      },
    },
    pagination: { pageSize: preferences.pageSize ?? 20 },
    selection: {},
  });

  useEffect(() => {
    setSelectedItems([]);
  }, []);

  const filterQuery = (propertyFilterProps as any)?.query || { tokens: [], operation: "or" };
  const hasActiveFilter = (filterQuery?.tokens || []).length > 0;

  useEffect(() => {
    if (!hasActiveFilter) {
      setCollectionSource(currentPage ?? []);
    }
  }, [hasActiveFilter, currentPage]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!hasActiveFilter) {
        setAllCampaignsForFilter([]);
        setIsFetchingAllForFilter(false);
        return;
      }

      setIsFetchingAllForFilter(true);

      try {
        const downloadQueryParams = globalUser?.Role !== "Admin" ? { Area: globalUser?.Area } : {};

        const all = await fetchAllPages<GetCampaignRes>(fetchGetCampaign as any, {
          queryParams: downloadQueryParams,
        });

        if (!cancelled) {
          setAllCampaignsForFilter(all);
          setCollectionSource(all);
        }
      } catch (e) {
        if (!cancelled) {
          setAllCampaignsForFilter([]);
          setCollectionSource([]);
        }
      } finally {
        if (!cancelled) setIsFetchingAllForFilter(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [hasActiveFilter, globalUser?.Role, globalUser?.Area]);

  const applyPropertyFilterAcrossAll = (data: GetCampaignRes[], query: any): GetCampaignRes[] => {
    const tokens = query?.tokens || [];
    const operation = (query?.operation || "or").toLowerCase();
    if (!tokens.length) return data;

    const getItemValue = (item: any, key: string) => {
      const v = item?.[key];
      if (v === null || v === undefined) return "";
      return String(v);
    };

    const matchToken = (item: any, token: any) => {
      const rawPropertyKey = token?.propertyKey || token?.property || token?.key;
      const propertyKey =
        rawPropertyKey && ["all", "any"].includes(String(rawPropertyKey).toLowerCase())
          ? undefined
          : rawPropertyKey;

      const operator = token?.operator;
      const value = (token?.value ?? "").toString().trim().toLowerCase();

      const fieldsToSearch = ["CampaignId", "Name", "Area", "State"];

      const compare = (fieldVal: string) => {
        const field = (fieldVal ?? "").toString().toLowerCase();
        if (operator === ":") return field.includes(value);
        if (operator === "=") return field === value;
        return field.includes(value);
      };

      if (!propertyKey) {
        return fieldsToSearch.some((k) => compare(getItemValue(item, k)));
      }
      return compare(getItemValue(item, propertyKey));
    };

    const matches = (item: any) => {
      if (operation === "and") return tokens.every((t: any) => matchToken(item, t));
      return tokens.some((t: any) => matchToken(item, t));
    };

    return data.filter(matches);
  };

  const applyUiSorting = (data: GetCampaignRes[]): GetCampaignRes[] => {
    const sortingColumn = (collectionProps as any)?.sortingColumn;
    const isDescending = !!(collectionProps as any)?.sortingDescending;

    const sortingField = sortingColumn?.sortingField;
    if (!sortingField) return data;

    const sorted = [...data].sort((a: any, b: any) => {
      const av = (a?.[sortingField] ?? "").toString().toLowerCase();
      const bv = (b?.[sortingField] ?? "").toString().toLowerCase();
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });

    return isDescending ? sorted.reverse() : sorted;
  };

  const onDownloadCampaigns = async () => {
    try {
      setDownloadLoading(true);

      notificationBar.clear();
      notificationBar.push({
        content: "Downloading campaigns CSV...",
        dismissible: false,
        loading: true,
        type: "info",
      });

      const downloadQueryParams = globalUser?.Role !== "Admin" ? { Area: globalUser?.Area } : {};
      const query = (propertyFilterProps as any)?.query || { tokens: [], operation: "or" };

      const baseDataForExport = hasActiveFilter
        ? allCampaignsForFilter
        : await fetchAllPages<GetCampaignRes>(fetchGetCampaign as any, {
            queryParams: downloadQueryParams,
          });

      const filteredAll = applyPropertyFilterAcrossAll(baseDataForExport, query);
      const exportList = applyUiSorting(filteredAll);

      const csvColumnMap: Record<string, { header: string; key: keyof GetCampaignRes }> = {
        campaign_id: { header: "ID", key: "CampaignId" },
        campaign_name: { header: "Name", key: "Name" },
        area: { header: "Area", key: "Area" },
        status: { header: "Status", key: "State" },
        leads_coll: { header: "Leads collected", key: "LeadsColl" },
        leads_disp: { header: "Leads dispatched", key: "LeadsDisp" },
        leads_lost: { header: "Leads lost", key: "LeadsLost" },
        user: { header: "Last Modified by", key: "User" },
      };

      const visibleColumnIds = (preferences.visibleContent || []) as string[];
      const csvColumns = visibleColumnIds
        .filter((id) => csvColumnMap[id])
        .map((id) => csvColumnMap[id]);

      const csvRows = exportList.map((c) => {
        const row: Record<string, any> = {};
        csvColumns.forEach((col) => {
          row[col.header] = (c as any)[col.key];
        });
        return row;
      });

      const csv = toCsv(
        csvRows,
        csvColumns.map((c) => c.header),
      );
      const timestamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);

      downloadCsvFile(csv, `lmp_campaigns_${timestamp}.csv`);

      notificationBar.clear();
      notificationBar.push({
        content: `Downloaded ${exportList.length} campaigns`,
        dismissible: true,
        type: "success",
      });
    } catch (error: any) {
      const message = error?.stack?.message || error?.message || `Unexpected error: ${error}`;

      notificationBar.clear();
      notificationBar.push({
        content: message,
        dismissible: true,
        type: "error",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const deleteCampaignModal = (
    <ConfirmModal
      title={selectedItems.length == 1 ? "Delete campaign" : "Delete campaigns"}
      warn={true}
      onClose={() => setDeleteModalVisible(false)}
      loading={deleteIsLoading}
      onConfirm={() => {
        setDeleteIsLoading(true);
        notificationBar.clear();
        notificationBar.push({
          content: "Deleting",
          dismissible: false,
          loading: true,
          type: "info",
        });
        fetchDeleteCampaign({ body: { CampaignIDs: selectedItems.map((x) => x.CampaignId) } })
          .finally(() => {
            setDeleteIsLoading(false);
            setDeleteModalVisible(false);
            notificationBar.clear();
          })
          .then((response: DeleteCampaignRes | undefined) => {
            setSelectedItems([]);
            resetPagination();
            if (response && response["CampaignIDs"].length > 0)
              notificationBar.push({
                content: `The following campaign(s) are deleted: ${response["CampaignIDs"]}`,
                dismissible: true,
                type: "success",
              });
            if (response && response["NotFound"].length > 0)
              notificationBar.push({
                content: `These IDs were not found: ${response["NotFound"]}`,
                dismissible: true,
                type: "error",
              });
            if (response && response["TransitionForbidden"].length > 0)
              notificationBar.push({
                content: `These campaigns cannot be deleted because they are not in "Draft" state: ${response["TransitionForbidden"]}`,
                dismissible: true,
                type: "error",
              });
          })
          .catch((error) => {
            const message = error?.stack?.message || `Unexpected error: ${error}`;
            notificationBar.push({
              content: message,
              dismissible: true,
              type: "error",
            });
          });
      }}
    >
      <div>
        <p>
          Permanently delete{" "}
          <strong style={{ color: "#e40501" }}>
            {selectedItems.map((x) => x.Name).join(", ")}
          </strong>
          ?
        </p>
        <p>This action can't be undone. All configurations will be lost.</p>
      </div>
    </ConfirmModal>
  );

  const [disableModalVisible, setDisableModalVisible] = useState(false);
  const [disableIsLoading, setDisableIsLoading] = useState(false);
  const disableCampaignModal = (
    <ConfirmModal
      title={selectedItems.length == 1 ? "Disable campaign" : "Disable campaigns"}
      onClose={() => setDisableModalVisible(false)}
      loading={disableIsLoading}
      onConfirm={() => {
        setDisableIsLoading(true);
        notificationBar.clear();
        notificationBar.push({
          content: `Disabling`,
          dismissible: false,
          loading: true,
          type: "info",
        });
        fetchDisableCampaign({ body: { CampaignIDs: selectedItems.map((x) => x.CampaignId) } })
          .finally(() => {
            setDisableIsLoading(false);
            setDisableModalVisible(false);
            notificationBar.clear();
          })
          .then((response: DeleteCampaignRes | undefined) => {
            setSelectedItems([]);
            resetPagination();
            if (response && response["CampaignIDs"].length > 0)
              notificationBar.push({
                content: `The following campaign(s) are disabled: ${response["CampaignIDs"]}`,
                dismissible: true,
                type: "success",
              });
            if (response && response["NotFound"].length > 0)
              notificationBar.push({
                content: `These CampaignIDs were not found: ${response["NotFound"]}`,
                dismissible: true,
                type: "error",
              });
            if (response && response["TransitionForbidden"].length > 0)
              notificationBar.push({
                content: `These campaigns cannot be disabled because they are not "Active": ${response["TransitionForbidden"]}`,
                dismissible: true,
                type: "error",
              });
          })
          .catch((error) => {
            const message = error?.stack?.message || `Unexpected error: ${error}`;
            notificationBar.push({
              content: message,
              dismissible: true,
              type: "error",
            });
          });
      }}
    >
      <div>
        <p>
          Temporarily disable{" "}
          <strong style={{ color: "#e40501" }}>
            {selectedItems.map((x) => x.Name).join(", ")}
          </strong>
          ?
        </p>
        <p>Leads from this page won’t be collected anymore. </p>
      </div>
    </ConfirmModal>
  );

  const [enableModalVisible, setEnableModalVisible] = useState(false);
  const [enableIsLoading, setEnableIsLoading] = useState(false);
  const enableCampaignModal = (
    <ConfirmModal
      title={selectedItems.length == 1 ? "Enable campaign" : "Enable campaigns"}
      onClose={() => setEnableModalVisible(false)}
      loading={enableIsLoading}
      onConfirm={() => {
        setEnableIsLoading(true);
        notificationBar.clear();
        notificationBar.push({
          content: `Enabling`,
          dismissible: false,
          loading: true,
          type: "info",
        });
        fetchEnableCampaign({ body: { CampaignIDs: selectedItems.map((x) => x.CampaignId) } })
          .finally(() => {
            setEnableIsLoading(false);
            setEnableModalVisible(false);
            notificationBar.clear();
          })
          .then((response: DeleteCampaignRes | undefined) => {
            setSelectedItems([]);
            resetPagination();
            if (response && response["CampaignIDs"].length > 0)
              notificationBar.push({
                content: `The following campaign(s) are enabled: ${response["CampaignIDs"]}`,
                dismissible: true,
                type: "success",
              });
            if (response && response["NotFound"].length > 0)
              notificationBar.push({
                content: `These CampaignIDs were not found: ${response["NotFound"]}`,
                dismissible: true,
                type: "error",
              });
            if (response && response["TransitionForbidden"].length > 0)
              notificationBar.push({
                content: `These campaigns cannot be enabled because they are not "Disabled": ${response["TransitionForbidden"]}`,
                dismissible: true,
                type: "error",
              });
          })
          .catch((error) => {
            const message = error?.stack?.message || `Unexpected error: ${error}`;
            notificationBar.push({
              content: message,
              dismissible: true,
              type: "error",
            });
          });
      }}
    >
      <div>
        <p>
          Re-enable{" "}
          <strong style={{ color: "#e40501" }}>
            {selectedItems.map((x) => x.Name).join(", ")}
          </strong>
          ?
        </p>
        <p>Leads from this page will return to be collected. </p>
      </div>
    </ConfirmModal>
  );

  const [launchModalVisible, setLaunchModalVisible] = useState(false);
  const [launchIsLoading, setLaunchIsLoading] = useState(false);
  const launchCampaignModal = (
    <ConfirmModal
      title={selectedItems.length == 1 ? "Launch campaign" : "Launch campaigns"}
      onClose={() => setLaunchModalVisible(false)}
      loading={launchIsLoading}
      onConfirm={() => {
        setLaunchIsLoading(true);
        notificationBar.clear();
        notificationBar.push({
          content: `Launching`,
          dismissible: false,
          loading: true,
          type: "info",
        });
        fetchLaunchCampaign({ body: { CampaignIDs: selectedItems.map((x) => x.CampaignId) } })
          .finally(() => {
            setLaunchIsLoading(false);
            setLaunchModalVisible(false);
            notificationBar.clear();
          })
          .then((response: DeleteCampaignRes | undefined) => {
            setSelectedItems([]);
            resetPagination();
            if (response && response["CampaignIDs"].length > 0)
              notificationBar.push({
                content: `The following campaigns(s) are launched: ${response["CampaignIDs"]}`,
                dismissible: true,
                type: "success",
              });
            if (response && response["NotFound"].length > 0)
              notificationBar.push({
                content: `These IDs were not found: ${response["NotFound"]}`,
                dismissible: true,
                type: "error",
              });
            if (response && response["TransitionForbidden"].length > 0)
              notificationBar.push({
                content: `These campaigns cannot be launched because they are not "Draft": ${response["TransitionForbidden"]}`,
                dismissible: true,
                type: "error",
              });
          })
          .catch((error) => {
            const message = error?.stack?.message || `Unexpected error: ${error}`;
            notificationBar.push({
              content: message,
              dismissible: true,
              type: "error",
            });
          });
      }}
    >
      <div>
        <p>
          Launch{" "}
          <strong style={{ color: "#e40501" }}>
            {selectedItems.map((x) => x.Name).join(", ")}
          </strong>
          ?
        </p>
        <p>The state will become active and leads data will be collected.</p>
      </div>
    </ConfirmModal>
  );

  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloneIsLoading, setCloneIsLoading] = useState(false);
  const cloneCampaignModal = (
    <ConfirmModal
      title="Clone campaign"
      onClose={() => setCloneModalVisible(false)}
      loading={cloneIsLoading}
      onConfirm={() => {
        setCloneIsLoading(true);
        notificationBar.clear();
        notificationBar.push({
          content: `Cloning`,
          dismissible: false,
          loading: true,
          type: "info",
        });
        fetchAddCampaign({
          queryParams: globalUser?.Role != "Admin" ? { Area: globalUser?.Area } : {},
          body: {
            Name: selectedItems[0].Name + " (copy)",
            Type: selectedItems[0].Type,
            Area: selectedItems[0].Area,
            EnrichFlag: selectedItems[0].EnrichFlag,
            Retention: selectedItems[0].Retention,
            TargetSys: selectedItems[0].TargetSys,
            DispatchMode: selectedItems[0].DispatchMode,
            HashFlag: selectedItems[0].HashFlag,
            HashAlg: selectedItems[0].HashAlg,
            EncAlg: selectedItems[0].EncAlg,
            User: globalUser?.Name + " " + globalUser?.Surname,
            Fields: selectedItems[0].Fields,
            VatLookupTolerance: selectedItems[0].VatLookupTolerance,
          },
        })
          .finally(() => {
            setCloneIsLoading(false);
            setCloneModalVisible(false);
            notificationBar.clear();
          })
          .then((campaign) => {
            setSelectedItems([]);
            resetPagination();
            notificationBar.push({
              content: `Campaign ${campaign.Name} (ID: ${campaign.CampaignId}) created`,
              dismissible: true,
              type: "success",
            });
            navigate("/campaigns");
          })
          .catch((error) => {
            const message = error?.stack?.message || `Unexpected error: ${error}`;
            notificationBar.push({
              content: message,
              dismissible: true,
              type: "error",
            });
          });
      }}
    >
      <div>
        <p>
          Clone{" "}
          <strong style={{ color: "#e40501" }}>
            {selectedItems.map((x) => x.Name).join(", ")}
          </strong>
          ?
        </p>
        <p>The campaign's properties will be the same, only the ID will change.</p>
      </div>
    </ConfirmModal>
  );

  return (
    <>
      {deleteModalVisible && deleteCampaignModal}
      {disableModalVisible && disableCampaignModal}
      {enableModalVisible && enableCampaignModal}
      {launchModalVisible && launchCampaignModal}
      {cloneModalVisible && cloneCampaignModal}

      {globalUser?.Role != "NONE" && (
        <Table
          {...collectionProps}
          variant="full-page"
          stickyHeader
          header={
            <TableHeader
              itemsCount={filteredItemsCount}
              actions={
                [
                  {
                    label: "Download CSV",
                    iconName: "download",

                    disabled: isLoading || downloadLoading || isFetchingAllForFilter,

                    onClick: () => onDownloadCampaigns(),
                  },

                  ...(globalUser?.Role != "Backoffice"
                    ? [
                        {
                          disabled: selectedItems.length === 0 || selectedItems.length > 1,
                          label: "Clone",
                          iconName: "copy",
                          onClick: () => setCloneModalVisible(true),
                        },
                        {
                          disabled:
                            selectedItems.length === 0 ||
                            (globalUser?.Role != "Admin"
                              ? selectedItems.findIndex((x) => x.State != CampaignStatus.Draft) !=
                                -1
                              : selectedItems.findIndex(
                                  (x) =>
                                    x.State != CampaignStatus.Draft &&
                                    x.State != CampaignStatus.Active &&
                                    x.State != CampaignStatus.Disabled,
                                ) != -1),
                          label: "Delete",
                          iconName: "remove",
                          onClick: () => setDeleteModalVisible(true),
                          warn: true,
                        },
                        {
                          disabled:
                            selectedItems.length === 0 ||
                            selectedItems.findIndex((x) => x.State != CampaignStatus.Active) != -1,
                          label: "Disable",
                          iconName: "undo",
                          onClick: () => setDisableModalVisible(true),
                        },
                        {
                          disabled:
                            selectedItems.length === 0 ||
                            selectedItems.findIndex((x) => x.State != CampaignStatus.Disabled) !=
                              -1,
                          label: "Enable",
                          iconName: "redo",
                          onClick: () => setEnableModalVisible(true),
                        },
                        {
                          disabled:
                            selectedItems.length === 0 ||
                            selectedItems.findIndex((x) => x.State != CampaignStatus.Draft) != -1,
                          label: "Launch",
                          iconName: "upload",
                          onClick: () => setLaunchModalVisible(true),
                        },
                        {
                          label: "Create",
                          iconName: "add-plus",
                          onClick: () => navigate("/campaigns/create"),
                        },
                      ]
                    : []),
                ] as any
              }
            >
              Campaigns
            </TableHeader>
          }
          filter={
            <PropertyFilter
              {...propertyFilterProps}
              filteringProperties={filteringProperties}
              i18nStrings={i18nStrings}
            />
          }
          loading={isLoading || isFetchingAllForFilter}
          contentDensity={preferences.contentDensity}
          visibleColumns={preferences.visibleContent}
          selectionType="multi"
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectedItems={selectedItems}
          columnDefinitions={columnDefinitions}
          items={items}
          loadingText="Loading campaigns"
          sortingDisabled={false}
          empty={
            <Box variant="p" color="text-status-info" textAlign="center">
              No campaigns to display.
            </Box>
          }
          pagination={<Pagination {...paginationProps} />}
          preferences={collectionPreferences}
        />
      )}

      {globalUser?.Role == "NONE" && (
        <Alert statusIconAriaLabel="Warning" type="warning">
          <h3 style={{ marginBottom: 0, marginTop: 0, marginLeft: 10 }}>
            {" "}
            Currently you don’t have any permission. Ask one of your administrators to give you
            access to the platform.
          </h3>
        </Alert>
      )}
    </>
  );
};

export default CampaignsPage;
